import { useCallback, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import axiosInstance, { keyLogin, weburl } from "../../axiosInstance";
import PublicLayout from "../components/PublicLayout";
import { Helmet } from "react-helmet";
import setCookie from "../../helpers/setCookie";
import { encrypt } from "../../helpers/encryption";
import LocationPicker from "../../components/LocationPicker";

/* ─── Brand tokens (gojobify) ─────────────────────────────────────────── */
const T = {
  purple: "#6B21A8",
  purpleMid: "#7C3AED",
  purpleLight: "#8B5CF6",
  purplePale: "#EDE9FE",
  purpleBorder: "#C4B5FD",
  orange: "#F97316",
  dark: "#0F0A1E",
  body: "#374151",
  muted: "#6B7280",
  border: "#E5E7EB",
  surface: "#F8F7FF",
  white: "#FFFFFF",
  error: "#DC2626",
  errorBg: "#FEF2F2",
  success: "#16A34A",
};

/* ─── Step config ────────────────────────────────────────────────────────── */
const STEPS = [
  { label: "Account Type", icon: "ri-user-settings-line" },
  { label: "Credentials",  icon: "ri-lock-line" },
  { label: "Personal",     icon: "ri-user-line" },
  { label: "Professional", icon: "ri-briefcase-line" },
  { label: "Documents",    icon: "ri-file-shield-2-line" },
  { label: "Review",       icon: "ri-check-double-line" },
];

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const capitalizeWords = (str) =>
  str
    ? str.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : str;

const emailRx = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

const getDashboardRoute = (type) => {
  if (Number(type) === 2) return "/job-seeker/dashboard";
  if (Number(type) === 1 || Number(type) === 3) return "/recruiter/dashboard";
  if (Number(type) === 0) return "/dashboard";
  return "/login";
};

const getListFromResponse = (response, keys = []) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  return [];
};

/* ─── Reusable field components ──────────────────────────────────────────── */
const Field = ({ label, required, error, children, half }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: half ? "0 0 calc(50% - 8px)" : "1 1 100%" }}>
    <label style={{ fontSize: 13, fontWeight: 600, color: T.dark }}>
      {label}{required && <span style={{ color: T.error, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {error && (
      <span style={{ fontSize: 12, color: T.error, display: "flex", alignItems: "center", gap: 4 }}>
        <i className="ri-error-warning-line" /> {error}
      </span>
    )}
  </div>
);

const inputStyle = (hasErr) => ({
  width: "100%",
  padding: "11px 14px",
  border: `1.5px solid ${hasErr ? T.error : T.border}`,
  borderRadius: 10,
  fontSize: 14,
  color: T.dark,
  background: T.white,
  outline: "none",
  fontFamily: "inherit",
  transition: "border-color .15s, box-shadow .15s",
});

const Input = ({ hasErr, onFocus, onBlur, style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputStyle(hasErr),
        ...(focused ? { borderColor: T.purpleLight, boxShadow: `0 0 0 3px ${T.purplePale}` } : {}),
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Select = ({ hasErr, children, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputStyle(hasErr),
        cursor: "pointer",
        ...(focused ? { borderColor: T.purpleLight, boxShadow: `0 0 0 3px ${T.purplePale}` } : {}),
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const Textarea = ({ hasErr, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...props}
      style={{
        ...inputStyle(hasErr),
        minHeight: 90,
        resize: "vertical",
        ...(focused ? { borderColor: T.purpleLight, boxShadow: `0 0 0 3px ${T.purplePale}` } : {}),
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

/* ─── Tag pill ───────────────────────────────────────────────────────────── */
const TagPill = ({ label, onRemove }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    background: T.purplePale, color: T.purple, border: `1px solid ${T.purpleBorder}`,
    borderRadius: 999, padding: "4px 12px", fontSize: 12, fontWeight: 600,
  }}>
    {label}
    <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color: T.purple, fontSize: 15, lineHeight: 1, padding: 0, display: "flex" }}>×</button>
  </span>
);

/* ─── Upload box ─────────────────────────────────────────────────────────── */
const UploadBox = ({ label, required, previewSrc, onChange, accept = ".jpg,.jpeg,.png,.webp", aspectRatio = "1/1" }) => {
  const [preview, setPreview] = useState(previewSrc || null);
  const id = label.replace(/\s+/g, "-").toLowerCase();
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    onChange(file);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: T.dark }}>
        {label}{required && <span style={{ color: T.error, marginLeft: 3 }}>*</span>}
      </label>
      <div
        onClick={() => document.getElementById(id).click()}
        style={{
          border: `1.5px dashed ${T.purpleBorder}`, borderRadius: 12, overflow: "hidden",
          background: T.purplePale, cursor: "pointer", transition: "border-color .15s",
        }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = T.purple}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = T.purpleBorder}
      >
        {preview ? (
          <img src={preview} alt={label} style={{ width: "100%", aspectRatio, objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, aspectRatio }}>
            <span style={{ width: 48, height: 48, borderRadius: "50%", background: `${T.purpleMid}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="ri-upload-cloud-2-line" style={{ fontSize: 22, color: T.purpleMid }} />
            </span>
            <span style={{ fontSize: 12, color: T.muted, textAlign: "center", lineHeight: 1.5 }}>
              <strong style={{ color: T.purpleMid }}>Click to upload</strong><br />
              {accept.replace(/\./g, "").toUpperCase()}
            </span>
          </div>
        )}
      </div>
      <input id={id} type="file" accept={accept} onChange={handleFile} style={{ display: "none" }} />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
const PublicSignupUser = () => {
  const navigate = useNavigate();

  /* ── State ────────────────────────────────────────────────────────────── */
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [nurseList, setNurseList] = useState([]);
  const [skillList, setSkillList] = useState([]);
  const [attrList, setAttrList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inputs, setInputs] = useState({
    type: "2",          // 2 = Job Seeker, 3 = Recruiter
    empType: "",
    username: "",
    phone: "",
    email: "",
    password: "",
    confirm_password: "",
    gender: "",
    DOB: "",
    state: "",
    statename: "",
    country: "",
    city: "",
    pincode: "",
    address: "",
    latitude: "",
    longitude: "",
    AltAddress: "",
    Salary: "",
    Experience: "",
    Education: "",
    MaritalStatus: "",
    location: "",
    Shift: [],
    DutyShift: [],
    nurse: [],
    skill: [],
    attribute: [],
    profile: null,
    AadhaarFront: null,
    AadhaarBack: null,
  });

  const isCandidate = inputs.empType === "4" && inputs.type === "1";

  /* ── Fetch dropdowns ───────────────────────────────────────────────────── */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let zoneList = [];

        try {
          const zonesResponse = await axiosInstance.get("/get-all-zones");
          zoneList = getListFromResponse(zonesResponse, ["Zones", "zones"]);
        } catch (zoneError) {
          console.error("Unable to load public zones:", zoneError);
        }

        if (!zoneList.length) {
          try {
            const adminZonesResponse = await axiosInstance.get("/admin/get-all-zones");
            zoneList = getListFromResponse(adminZonesResponse, ["Zones", "zones"]);
          } catch (adminZoneError) {
            console.error("Unable to load admin zones fallback:", adminZoneError);
          }
        }

        setStates(
          zoneList
            .filter((zone) => zone?._id && zone?.name)
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        );

        const [n, s, a] = await Promise.allSettled([
          axiosInstance.get("/get-all-nurse-department"),
          axiosInstance.get("/get-all-nurse-skill-department"),
          axiosInstance.get("/get-all-nurse-attributes-department"),
        ]);

        setNurseList(n.status === "fulfilled" ? getListFromResponse(n.value, ["Department", "department"]) : []);
        setSkillList(s.status === "fulfilled" ? getListFromResponse(s.value, ["Department", "department"]) : []);
        setAttrList(a.status === "fulfilled" ? getListFromResponse(a.value, ["Department", "department"]) : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Input handlers ──────────────────────────────────────────────────── */
  const set = (name, value) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") { set(name, files[0]); return; }

    if (name === "username") { set(name, capitalizeWords(value)); return; }

    if (name === "state") {
      const found = states.find((s) => s._id === value);
      setInputs((prev) => ({ ...prev, state: value, statename: found?.name || "", city: "" }));
      return;
    }

    // Multi-select push
    if (["nurse", "skill", "attribute", "Shift", "DutyShift"].includes(name)) {
      if (!value || value === "null") return;
      setInputs((prev) => {
        const arr = Array.isArray(prev[name]) ? prev[name] : [];
        if (arr.includes(value)) return prev;
        return { ...prev, [name]: [...arr, value] };
      });
      e.target.value = "";
      return;
    }

    set(name, value);
  };

  const removeFromArray = (name, val) =>
    setInputs((prev) => ({ ...prev, [name]: prev[name].filter((v) => v !== val) }));

  /* ── Validation per step ─────────────────────────────────────────────── */
  const validate = () => {
    const errs = {};
    const req = (k, msg) => { if (!inputs[k] || (Array.isArray(inputs[k]) && !inputs[k].length)) errs[k] = msg; };

    if (step === 2) {
      req("username", "Full name is required");
      if (!inputs.phone) errs.phone = "Phone is required";
      else if (!/^\d{10}$/.test(inputs.phone)) errs.phone = "Enter a valid 10-digit number";
      if (!inputs.email) errs.email = "Email is required";
      else if (!emailRx.test(inputs.email)) errs.email = "Enter a valid email address";
      req("password", "Password is required");
      if (!inputs.confirm_password) errs.confirm_password = "Please confirm your password";
      else if (inputs.password !== inputs.confirm_password) errs.confirm_password = "Passwords do not match";
    }

    if (step === 3) {
      req("gender", "Gender is required");
      if (!inputs.DOB) { errs.DOB = "Date of birth is required"; }
      else {
        const dob = new Date(inputs.DOB);
        const age = new Date().getFullYear() - dob.getFullYear();
        if (age < 18) errs.DOB = "You must be at least 18 years old";
      }
      req("state", "State is required");
      req("city", "City is required");
      req("pincode", "Pincode is required");
      req("address", "Address is required");
    }

    if (step === 4 && isCandidate) {
      req("Salary", "Salary is required");
      req("Experience", "Experience is required");
      req("Education", "Education is required");
      req("Shift", "Select at least one shift");
      req("DutyShift", "Select at least one duty duration");
      req("nurse", "Select at least one candidate category");
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Step navigation ─────────────────────────────────────────────────── */
  const checkContactExists = async () => {
    const { data } = await axiosInstance.post("/check-user-contact", {
      email: inputs.email,
      phone: inputs.phone,
    });

    if (data.emailExists) {
      setErrors((prev) => ({ ...prev, email: "Email already exists" }));
      toast.error("Email already exists");
      return true;
    }

    if (data.phoneExists) {
      setErrors((prev) => ({ ...prev, phone: "Phone already exists" }));
      toast.error("Phone already exists");
      return true;
    }

    return false;
  };

  const applyLocation = useCallback((location) => {
    setInputs((prev) => ({
      ...prev,
      ...location,
      state: location.state || prev.state,
      statename: location.statename || prev.statename,
    }));
    setErrors((prev) => ({
      ...prev,
      state: "",
      city: "",
      country: "",
      pincode: "",
      address: "",
    }));
  }, []);

  const next = async () => {
    if (!validate()) return;

    if (step === 2) {
      try {
        const exists = await checkContactExists();
        if (exists) return;
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to check email/phone");
        return;
      }
    }

    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  };
  const back = () => { setStep((s) => s - 1); window.scrollTo(0, 0); };

  /* ── Submit ──────────────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      [
        "type",
        "username",
        "phone",
        "email",
        "state",
        "statename",
        "country",
        "latitude",
        "longitude",
        "password",
        "confirm_password",
        "pincode",
        "gender",
        "DOB",
        "address",
        "city",
      ].forEach((key) => {
        formData.append(key, inputs[key] || "");
      });

      if (inputs.profile instanceof File) formData.append("profile", inputs.profile);
      if (inputs.AadhaarFront instanceof File) formData.append("AadhaarFront", inputs.AadhaarFront);
      if (inputs.AadhaarBack instanceof File) formData.append("AadhaarBack", inputs.AadhaarBack);

      const { data } = await axiosInstance.post("/signup-user-type", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.success) {
        try {
          const loginResponse = await axiosInstance.post("/user-login-all", {
            email: inputs.email,
            password: inputs.password,
          });

          const loggedInUser = loginResponse.data?.admin;
          if (loginResponse.data?.success && loggedInUser) {
            const { _id, username, email, type, token, normalizedRole } = loggedInUser;
            const userToStore = { _id, username, email, type, token, normalizedRole };
            const encryptedData = encrypt(JSON.stringify(userToStore), keyLogin);
            setCookie("token", encryptedData);
            window.dispatchEvent(
              new CustomEvent("ynb-auth-changed", {
                detail: userToStore,
              })
            );

            toast.success("Account created successfully!");
            navigate(getDashboardRoute(type), { replace: true });
            return;
          }
        } catch (loginError) {
          console.error("Auto-login after signup failed:", loginError);
        }

        toast.success("Account created successfully. Please login to continue.");
        navigate("/login", { replace: true, state: { email: inputs.email } });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Multi-select rendered tags ──────────────────────────────────────── */
  const TagRow = ({ name, list, emptyText }) => {
    const ids = inputs[name] || [];
    if (!ids.length) return <span style={{ fontSize: 12, color: T.muted, fontStyle: "italic" }}>{emptyText}</span>;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {ids.map((id) => {
          const found = list?.find((x) => x._id === id);
          return found ? (
            <TagPill key={id} label={found.name} onRemove={() => removeFromArray(name, id)} />
          ) : null;
        })}
      </div>
    );
  };

  const ShiftTags = ({ name }) => {
    const vals = inputs[name] || [];
    if (!vals.length) return <span style={{ fontSize: 12, color: T.muted, fontStyle: "italic" }}>None selected</span>;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {vals.map((v) => <TagPill key={v} label={v} onRemove={() => removeFromArray(name, v)} />)}
      </div>
    );
  };

  /* ── Styles helpers ──────────────────────────────────────────────────── */
  const card = {
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    padding: "32px 32px 28px",
    boxShadow: "0 4px 24px rgba(107,33,168,.07)",
  };

  const btnPrimary = (disabled) => ({
    background: disabled ? "#C4B5FD" : `linear-gradient(135deg, ${T.purple}, ${T.purpleLight})`,
    border: "none",
    color: T.white,
    borderRadius: 10,
    padding: "12px 28px",
    fontSize: 15,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "opacity .15s",
  });

  const btnSecondary = {
    background: T.white,
    border: `1.5px solid ${T.border}`,
    color: T.body,
    borderRadius: 10,
    padding: "12px 24px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  /* ──────────────────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────────────────── */
  return (
    <PublicLayout>
      <Helmet>
        <title>Sign up | gojobify</title>
        <meta name="description" content="Create your gojobify account and start finding jobs or hiring talent today." />
      </Helmet>

      {/* ── Page shell ── */}
      <div style={{ background: `linear-gradient(160deg, ${T.purplePale} 0%, ${T.white} 50%, ${T.surface} 100%)`, minHeight: "100vh", paddingBottom: 80 }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 16px 0" }}>

          {/* ── Page header ── */}
          <div style={{ marginBottom: 32, textAlign: "center" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 8, background: T.purplePale,
              color: T.purple, borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700,
              border: `1px solid ${T.purpleBorder}`, marginBottom: 16,
            }}>
              <i className="ri-rocket-2-line" /> Join gojobify
            </span>
            <h1 style={{ fontSize: "clamp(1.7rem, 4vw, 2.6rem)", fontWeight: 900, color: T.dark, lineHeight: 1.1, margin: 0 }}>
              Create your{" "}
              <span style={{ color: T.purpleMid }}>free account</span>
            </h1>
            <p style={{ color: T.muted, marginTop: 10, fontSize: 15 }}>
              Find better opportunities or hire top talent — it starts here.
            </p>
          </div>

          {/* ── Step indicator ── */}
          <div style={{ position: "relative", marginBottom: 32 }}>
            {/* Progress line */}
            <div style={{ position: "absolute", top: 20, left: "10%", right: "10%", height: 2, background: T.border, zIndex: 0 }} />
            <div style={{
              position: "absolute", top: 20, left: "10%", height: 2, zIndex: 1,
              background: `linear-gradient(90deg, ${T.purple}, ${T.purpleLight})`,
              width: `${((step - 1) / (STEPS.length - 1)) * 80}%`,
              transition: "width .4s ease",
            }} />

            <div style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
              {STEPS.map((s, i) => {
                const n = i + 1;
                const done = n < step;
                const active = n === step;
                return (
                  <div key={s.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: done ? T.purple : active ? T.white : T.white,
                      border: `2px solid ${done || active ? T.purple : T.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: active ? `0 0 0 4px ${T.purplePale}` : "none",
                      transition: "all .25s",
                    }}>
                      {done
                        ? <i className="ri-check-line" style={{ color: T.white, fontSize: 16 }} />
                        : <i className={s.icon} style={{ color: active ? T.purple : T.muted, fontSize: 15 }} />
                      }
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap",
                      color: done || active ? T.purple : T.muted,
                      display: "block",
                    }}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ═══════════ STEP 1 — Account type ═══════════ */}
          {step === 1 && (
            <div style={card}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: T.dark, marginBottom: 4 }}>Choose account type</h2>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24 }}>Select the role that best describes you</p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 4 }}>
                {[
                  { val: "2", label: "Job Seeker", desc: "Browse and apply to job listings", icon: "ri-briefcase-4-line" },
                  { val: "3", label: "Recruiter",  desc: "Post jobs and hire top talent",    icon: "ri-team-line" },
                ].map((opt) => {
                  const sel = inputs.type === opt.val;
                  return (
                    <div
                      key={opt.val}
                      onClick={() => set("type", opt.val)}
                      style={{
                        border: `2px solid ${sel ? T.purple : T.border}`,
                        borderRadius: 14, padding: 24, cursor: "pointer",
                        background: sel ? T.purplePale : T.white,
                        display: "flex", flexDirection: "column", alignItems: "center",
                        gap: 12, textAlign: "center",
                        transition: "all .2s",
                        boxShadow: sel ? `0 0 0 4px ${T.purplePale}` : "none",
                      }}
                    >
                      <div style={{
                        width: 64, height: 64, borderRadius: "50%",
                        background: sel ? `linear-gradient(135deg, ${T.purple}, ${T.purpleLight})` : "#F3F4F6",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <i className={opt.icon} style={{ fontSize: 28, color: sel ? T.white : T.muted }} />
                      </div>
                      <div>
                        <p style={{ fontWeight: 800, fontSize: 16, color: T.dark, margin: 0 }}>{opt.label}</p>
                        <p style={{ fontSize: 13, color: T.muted, margin: "4px 0 0" }}>{opt.desc}</p>
                      </div>
                      {sel && (
                        <span style={{
                          background: T.purple, color: T.white, borderRadius: 999,
                          padding: "3px 14px", fontSize: 11, fontWeight: 700,
                        }}>Selected ✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══════════ STEP 2 — Credentials ═══════════ */}
          {step === 2 && (
            <div style={card}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: T.dark, marginBottom: 4 }}>Account credentials</h2>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24 }}>Set up your login details</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Field label="Full name" required error={errors.username}>
                  <Input name="username" value={inputs.username} onChange={handleChange} placeholder="Priya Sharma" hasErr={!!errors.username} />
                </Field>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <Field label="Phone" required half error={errors.phone}>
                    <Input
                      name="phone" type="tel" value={inputs.phone} hasErr={!!errors.phone}
                      onChange={(e) => { if (e.target.value.length <= 10) handleChange(e); }}
                      placeholder="9876543210" maxLength={10}
                    />
                  </Field>
                  <Field label="Email" required half error={errors.email}>
                    <Input name="email" type="email" value={inputs.email} onChange={handleChange} placeholder="priya@email.com" hasErr={!!errors.email} />
                  </Field>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <Field label="Password" required half error={errors.password}>
                    <div style={{ position: "relative" }}>
                      <Input
                        name="password" type={showPw ? "text" : "password"} value={inputs.password}
                        onChange={handleChange} placeholder="Min. 8 characters" hasErr={!!errors.password}
                        style={{ paddingRight: 44 }}
                      />
                      <button
                        onClick={() => setShowPw((v) => !v)} type="button"
                        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.muted, fontSize: 18, display: "flex" }}
                      >
                        <i className={showPw ? "ri-eye-off-line" : "ri-eye-line"} />
                      </button>
                    </div>
                  </Field>
                  <Field label="Confirm password" required half error={errors.confirm_password}>
                    <div style={{ position: "relative" }}>
                      <Input
                        name="confirm_password" type={showPw ? "text" : "password"} value={inputs.confirm_password}
                        onChange={handleChange} placeholder="Repeat password" hasErr={!!errors.confirm_password}
                        style={{ paddingRight: 44 }}
                      />
                    </div>
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════ STEP 3 — Personal ═══════════ */}
          {step === 3 && (
            <div style={card}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: T.dark, marginBottom: 4 }}>Personal details</h2>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24 }}>Tell us a bit about yourself</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <Field label="Gender" required half error={errors.gender}>
                    <Select name="gender" value={inputs.gender} onChange={handleChange} hasErr={!!errors.gender}>
                      <option value="">Select gender</option>
                      <option value="1">Male</option>
                      <option value="2">Female</option>
                      <option value="0">Other</option>
                    </Select>
                  </Field>
                  <Field label="Date of birth" required half error={errors.DOB}>
                    <Input name="DOB" type="date" value={inputs.DOB} onChange={handleChange} hasErr={!!errors.DOB} />
                  </Field>
                </div>

                <LocationPicker
                  value={inputs.address}
                  zones={states}
                  onSelect={applyLocation}
                  label="Search address on map"
                />

                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <Field label="State" required half error={errors.state}>
                    <Select name="state" value={inputs.state} onChange={handleChange} hasErr={!!errors.state}>
                      <option value="">{loading ? "Loading…" : "Select state"}</option>
                      {states.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                    </Select>
                  </Field>
                  <Field label="City" required half error={errors.city}>
                    <Input name="city" value={inputs.city} onChange={handleChange} placeholder="Mumbai" hasErr={!!errors.city} />
                  </Field>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <Field label="Pincode" required half error={errors.pincode}>
                    <Input name="pincode" value={inputs.pincode} onChange={handleChange} placeholder="400001" maxLength={6} hasErr={!!errors.pincode} />
                  </Field>
                  <Field label="Marital status" half>
                    <Select name="MaritalStatus" value={inputs.MaritalStatus} onChange={handleChange}>
                      <option value="">Select status</option>
                      <option value="Married">Married</option>
                      <option value="Unmarried">Unmarried</option>
                    </Select>
                  </Field>
                </div>

                <Field label="Address" required error={errors.address}>
                  <Textarea name="address" value={inputs.address} onChange={handleChange} placeholder="Street, area, landmark…" hasErr={!!errors.address} />
                </Field>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <Field label="Country" half>
                    <Input name="country" value={inputs.country} onChange={handleChange} placeholder="India" />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════ STEP 4 — Professional ═══════════ */}
          {step === 4 && (
            <div style={card}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: T.dark, marginBottom: 4 }}>Professional info</h2>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24 }}>Your work preferences and background</p>

              {isCandidate ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <Field label="Expected salary (₹/month)" required half error={errors.Salary}>
                      <Input name="Salary" type="number" value={inputs.Salary} onChange={handleChange} placeholder="25000" hasErr={!!errors.Salary} />
                    </Field>
                    <Field label="Experience (years)" required half error={errors.Experience}>
                      <Input name="Experience" type="number" value={inputs.Experience} onChange={handleChange} placeholder="2" min="0" hasErr={!!errors.Experience} />
                    </Field>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <Field label="Education" required half error={errors.Education}>
                      <Input name="Education" value={inputs.Education} onChange={handleChange} placeholder="B.Sc Nursing" hasErr={!!errors.Education} />
                    </Field>
                    <Field label="Location preference" half>
                      <Input name="location" value={inputs.location} onChange={handleChange} placeholder="Bengaluru, Remote…" />
                    </Field>
                  </div>

                  <Field label="Current address">
                    <Textarea name="AltAddress" value={inputs.AltAddress} onChange={handleChange} placeholder="Current residential address" />
                  </Field>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    {/* Shift */}
                    <Field label="Preferred shift" required half error={errors.Shift}>
                      <Select name="Shift" value="" onChange={handleChange} hasErr={!!errors.Shift}>
                        <option value="">Select shift</option>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                      <div style={{ marginTop: 6 }}>
                        <ShiftTags name="Shift" />
                      </div>
                    </Field>
                    {/* Duty shift */}
                    <Field label="Duty hours" required half error={errors.DutyShift}>
                      <Select name="DutyShift" value="" onChange={handleChange} hasErr={!!errors.DutyShift}>
                        <option value="">Select hours</option>
                        <option value="8 Hours">8 hours</option>
                        <option value="10 Hours">10 hours</option>
                        <option value="12 Hours">12 hours</option>
                        <option value="24 Hours">24 hours</option>
                      </Select>
                      <div style={{ marginTop: 6 }}>
                        <ShiftTags name="DutyShift" />
                      </div>
                    </Field>
                  </div>

                  {/* Candidate category */}
                  <Field label="Candidate category" required error={errors.nurse}>
                    <Select name="nurse" value="null" onChange={handleChange} hasErr={!!errors.nurse}>
                      <option value="null">Select category</option>
                      {nurseList.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                    </Select>
                    <div style={{ marginTop: 8 }}>
                      <TagRow name="nurse" list={nurseList} emptyText="No category selected" />
                    </div>
                  </Field>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <Field label="Skills" half>
                      <Select name="skill" value="null" onChange={handleChange}>
                        <option value="null">Select skill</option>
                        {skillList.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                      </Select>
                      <div style={{ marginTop: 8 }}>
                        <TagRow name="skill" list={skillList} emptyText="No skill selected" />
                      </div>
                    </Field>
                    <Field label="Attributes" half>
                      <Select name="attribute" value="null" onChange={handleChange}>
                        <option value="null">Select attribute</option>
                        {attrList.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                      </Select>
                      <div style={{ marginTop: 8 }}>
                        <TagRow name="attribute" list={attrList} emptyText="No attribute selected" />
                      </div>
                    </Field>
                  </div>
                </div>
              ) : (
                /* Non-candidate or recruiter */
                <div style={{
                  background: T.purplePale, border: `1px solid ${T.purpleBorder}`,
                  borderRadius: 12, padding: 20, display: "flex", gap: 14, alignItems: "flex-start",
                }}>
                  <i className="ri-information-line" style={{ fontSize: 22, color: T.purple, flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p style={{ fontWeight: 700, color: T.purple, margin: "0 0 4px" }}>Professional details</p>
                    <p style={{ color: T.body, fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                      You can fill in your professional details — skills, experience, and preferences — from your dashboard after your account is created.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════ STEP 5 — Documents ═══════════ */}
          {step === 5 && (
            <div style={card}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: T.dark, marginBottom: 4 }}>Upload documents</h2>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24 }}>Identity verification documents (JPG, PNG, PDF)</p>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
                <UploadBox
                  label="Profile photo" required
                  previewSrc={inputs.profile && typeof inputs.profile !== "string" ? null : (inputs.profile ? weburl + inputs.profile : null)}
                  onChange={(f) => set("profile", f)}
                  accept=".jpg,.jpeg,.png,.webp"
                  aspectRatio="1/1"
                />
                <UploadBox
                  label="Aadhaar front" required
                  previewSrc={inputs.AadhaarFront && typeof inputs.AadhaarFront === "string" ? weburl + inputs.AadhaarFront : null}
                  onChange={(f) => set("AadhaarFront", f)}
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  aspectRatio="4/3"
                />
                <UploadBox
                  label="Aadhaar back" required
                  previewSrc={inputs.AadhaarBack && typeof inputs.AadhaarBack === "string" ? weburl + inputs.AadhaarBack : null}
                  onChange={(f) => set("AadhaarBack", f)}
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  aspectRatio="4/3"
                />
              </div>
            </div>
          )}

          {/* ═══════════ STEP 6 — Review ═══════════ */}
          {step === 6 && (
            <div style={card}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: T.dark, marginBottom: 4 }}>Review your details</h2>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24 }}>Check everything before creating your account</p>

              {[
                {
                  title: "Account",
                  rows: [
                    ["Account type", inputs.type === "2" ? "Job Seeker" : "Recruiter"],
                    ["Full name", inputs.username],
                    ["Email", inputs.email],
                    ["Phone", inputs.phone],
                  ],
                },
                {
                  title: "Personal",
                  rows: [
                    ["Gender", inputs.gender === "1" ? "Male" : inputs.gender === "2" ? "Female" : "Other"],
                    ["Date of birth", inputs.DOB],
                    ["State", inputs.statename],
                    ["City", inputs.city],
                    ["Pincode", inputs.pincode],
                  ],
                },
                ...(isCandidate ? [{
                  title: "Professional",
                  rows: [
                    ["Salary", inputs.Salary ? `₹${Number(inputs.Salary).toLocaleString()}/mo` : "—"],
                    ["Experience", inputs.Experience ? `${inputs.Experience} yr(s)` : "—"],
                    ["Education", inputs.Education],
                    ["Shifts", inputs.Shift.join(", ") || "—"],
                    ["Duty hours", inputs.DutyShift.join(", ") || "—"],
                  ],
                }] : []),
              ].map((section) => (
                <div key={section.title} style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 11, fontWeight: 800, color: T.purple, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
                    {section.title}
                  </p>
                  <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                    {section.rows.filter(([, v]) => v).map(([k, v], i, arr) => (
                      <div key={k} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "10px 16px", fontSize: 14,
                        borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none",
                        background: i % 2 === 0 ? T.white : T.surface,
                      }}>
                        <span style={{ color: T.muted, fontWeight: 500 }}>{k}</span>
                        <span style={{ color: T.dark, fontWeight: 600, textAlign: "right", maxWidth: "55%" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Terms notice */}
              <div style={{
                background: T.purplePale, border: `1px solid ${T.purpleBorder}`,
                borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start",
              }}>
                <i className="ri-shield-check-line" style={{ color: T.purple, fontSize: 18, flexShrink: 0, marginTop: 2 }} />
                <p style={{ fontSize: 13, color: T.body, margin: 0, lineHeight: 1.6 }}>
                  By creating an account you agree to gojobify's <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>. Your data is encrypted and never shared without your consent.
                </p>
              </div>
            </div>
          )}

          {/* ═══════════ Footer nav ═══════════ */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginTop: 20, padding: "16px 0",
          }}>
            <div>
              {step > 1 && (
                <button onClick={back} style={btnSecondary}>
                  <i className="ri-arrow-left-line" /> Back
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
                Step {step} of {STEPS.length}
              </span>
              {step < STEPS.length ? (
                <button onClick={next} style={btnPrimary(false)}>
                  Continue <i className="ri-arrow-right-line" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={submitting} style={btnPrimary(submitting)}>
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Creating…
                    </>
                  ) : (
                    <><i className="ri-user-add-line" /> Create account</>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ═══════════ Trust strip ═══════════ */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))",
            gap: 12, marginTop: 8,
          }}>
            {[
              { icon: "ri-shield-check-line",       title: "100% Secure",    text: "Industry-standard encryption" },
              { icon: "ri-verified-badge-line",      title: "Verified jobs",  text: "Only genuine listings" },
              { icon: "ri-customer-service-2-line",  title: "24/7 Support",   text: "We're always here" },
              { icon: "ri-lock-password-line",       title: "Private data",   text: "Never sold or shared" },
            ].map((item) => (
              <div key={item.title} style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: "14px 16px", display: "flex", gap: 12, alignItems: "center",
              }}>
                <span style={{
                  width: 40, height: 40, borderRadius: "50%", background: T.purplePale, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className={item.icon} style={{ fontSize: 18, color: T.purple }} />
                </span>
                <div>
                  <strong style={{ display: "block", fontSize: 13, color: T.dark }}>{item.title}</strong>
                  <span style={{ display: "block", fontSize: 11, color: T.muted, lineHeight: 1.4 }}>{item.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PublicSignupUser;
